/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Tooltip, useTheme } from '@mui/material';
import React from 'react';
import { getBlockStyle } from './warningBlockColors';
import { getBlockSize } from './warningsTimelineUtils';
import type { Warning } from '../../store/warningsForm/types';
import { useWarningsTranslation } from '../../utils/i18n';
import type { WarningLevel } from '../PublicWarningsForm/LevelField';
import {
  getPhenomenaIcon,
  warningPhenomena,
} from '../PublicWarningsForm/PhenomenonField';
import WarningsAvatar from '../PublicWarningsFormDialog/WarningsAvatar';

interface WarningBlockProps {
  warning: Warning;
  highlighted?: boolean;
  left: number;
  onHoverWarning?: (warningId?: string) => void;
  top: number;
  width: number;
}

/**
 * Gets the author/editor from a warning.
 * For airmet/sigmet warnings, author is in sigmetAirmetDetails.
 * For public/maritime warnings, editor is on the base warning.
 */
const getWarningAuthor = (warning: Warning): string | undefined => {
  if ('sigmetAirmetDetails' in warning) {
    return warning.sigmetAirmetDetails?.author ?? warning.editor;
  }
  return warning.editor;
};

const formatPeriod = (from: string, until: string): string => {
  const ff = new Date(from);
  const uu = new Date(until);
  const dateOpts: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  };
  const timeOpts: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  };
  const fDate = ff.toLocaleDateString('en-GB', dateOpts);
  const uDate = uu.toLocaleDateString('en-GB', dateOpts);
  const fTime = ff.toLocaleTimeString('en-GB', timeOpts);
  const uTime = uu.toLocaleTimeString('en-GB', timeOpts);
  return fDate === uDate
    ? `${fDate} ${fTime} – ${uTime} UTC`
    : `${fDate} ${fTime} – ${uDate} ${uTime} UTC`;
};

const getTooltipContent = (
  warning: Warning,
  t: (key: string) => string,
): React.ReactNode => {
  const { warningDetail, status, type } = warning;
  const author = getWarningAuthor(warning);
  const isAviation = type === 'sigmet' || type === 'airmet';
  let warningTypeLabel: string | null = null;
  if (type === 'maritimeWarningCoastal' || type === 'maritimeWarningNorthSea') {
    warningTypeLabel = t('timeline-tooltip-maritime-warning');
  } else if (!isAviation) {
    warningTypeLabel = t('timeline-tooltip-public-warning');
  }

  const phenomenonEntry = warningPhenomena.find(
    (entry) => entry.key === warningDetail.phenomenon,
  );
  const phenomenonLabel = phenomenonEntry
    ? t(phenomenonEntry.translationKey)
    : warningDetail.phenomenon;

  const line1 = isAviation
    ? [type.toUpperCase(), phenomenonLabel].filter(Boolean).join(' · ')
    : [
        phenomenonLabel,
        warningDetail.level,
        warningDetail.probability ? `${warningDetail.probability}%` : null,
      ]
        .filter(Boolean)
        .join(' · ');

  const period =
    warningDetail.validFrom && warningDetail.validUntil
      ? formatPeriod(warningDetail.validFrom, warningDetail.validUntil)
      : null;

  const meta = [author, status].filter(Boolean).join(' · ');

  return (
    <Box sx={{ fontSize: 12, lineHeight: 1.6 }}>
      {warningTypeLabel ? (
        <Box sx={{ fontWeight: 600 }}>{warningTypeLabel}</Box>
      ) : null}
      <Box sx={{ fontWeight: warningTypeLabel ? 400 : 600 }}>{line1}</Box>
      {period ? <Box>{period}</Box> : null}
      {meta ? <Box>{meta}</Box> : null}
    </Box>
  );
};

export const WarningBlock = React.memo(
  ({
    warning,
    highlighted = false,
    left,
    onHoverWarning,
    top,
    width,
  }: WarningBlockProps) => {
    const { t } = useWarningsTranslation();
    const theme = useTheme();
    const { warningDetail, status, type } = warning;
    const author = getWarningAuthor(warning);
    const isAviation = type === 'sigmet' || type === 'airmet';
    const level = (warningDetail.level as WarningLevel) || null;
    const letter = level ? t(`warning-level-letter-${level}`) : '';

    const { background, border, iconColor, letterColor } = getBlockStyle(
      level,
      status,
      isAviation,
    );

    const size = getBlockSize(width);
    const highlightOutlineColor =
      theme.palette.geowebColors.typographyAndIcons.iconLinkActive;

    const elements: React.ReactNode[] = [];

    if (size === 's' || size === 'l') {
      elements.push(
        <Box
          key="letter"
          sx={{
            fontSize: 16,
            fontWeight: 600,
            lineHeight: '80%',
            color: letterColor,
          }}
        >
          {letter}
        </Box>,
      );
    }

    if (size !== 'xxs') {
      elements.push(
        <Box
          key="icon"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: iconColor,
            fontSize: 18,
          }}
        >
          {getPhenomenaIcon(warningDetail.phenomenon) || null}
        </Box>,
      );
    }

    if (size === 'l') {
      elements.push(
        <Box
          key="avatar"
          sx={{ border: '0.5px solid white', borderRadius: '50%' }}
        >
          <WarningsAvatar editor={author ?? ''} />
        </Box>,
      );
    }

    return (
      <Tooltip placement="top" title={getTooltipContent(warning, t)}>
        <Box
          data-highlighted={highlighted ? 'true' : 'false'}
          data-testid="warning-block"
          onMouseEnter={() => onHoverWarning?.(warning.id)}
          onMouseLeave={() => onHoverWarning?.(undefined)}
          sx={{
            position: 'absolute',
            left,
            top,
            width,
            height: 28,
            padding: '4px 8px',
            background,
            border,
            borderRadius: 1,
            boxShadow: highlighted
              ? `0 0 0 2px ${highlightOutlineColor}`
              : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: elements.length === 1 ? 'center' : 'space-between',
            overflow: 'hidden',
            zIndex: highlighted ? 11 : 1,
            '&:hover': {
              boxShadow: `0 0 0 2px ${highlightOutlineColor}`,
              zIndex: 11,
            },
          }}
        >
          {elements}
        </Box>
      </Tooltip>
    );
  },
);
